import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, LogOut, Eye, ChevronLeft, Users, FileCheck, Clock, Target, TrendingUp, BarChart3, Cog, Database, Megaphone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import axios from "axios";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/components/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pillarIcons = {
  "Strategic Direction": Target,
  "Marketing System": Megaphone,
  "Sales System": TrendingUp,
  "Operations System": Cog,
  "Systems & Automation": Database,
  "Data & Reporting": BarChart3,
};

const ratingColors = {
  "Optimised": "#94CE77",
  "Advanced": "#9CCEFF",
  "Structured": "#02808F",
  "Developing": "#FDB714",
  "Poor": "#DC0706",
};

const statusLabels = {
  "started": { label: "Started", color: "#52525B" },
  "submitted": { label: "Answered", color: "#FDB714" },
  "report_ready": { label: "Report Ready", color: "#94CE77" },
};

const webhookLabels = {
  "pending": { label: "Pending", color: "#52525B" },
  "delivered": { label: "CRM Sent", color: "#94CE77" },
  "failed": { label: "CRM Failed", color: "#DC0706" },
};

export default function AdminDashboard() {
  const [authenticated, setAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [audits, setAudits] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, in_progress: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { logos, logoStyle } = useTheme();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      await axios.post(`${API}/admin/login`, {}, { headers: { "x-admin-key": adminKey } });
      setAuthenticated(true);
      loadAudits();
    } catch {
      setLoginError("Invalid admin key");
    } finally {
      setLoginLoading(false);
    }
  };

  const loadAudits = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audits`, { headers: { "x-admin-key": adminKey } });
      setAudits(res.data.audits);
      setStats(res.data.stats);
    } catch (err) {
      console.error("Failed to load audits:", err);
    } finally {
      setLoading(false);
    }
  };

  const viewAuditDetail = async (auditId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audits/${auditId}`, { headers: { "x-admin-key": adminKey } });
      setSelectedAudit(res.data);
    } catch (err) {
      console.error("Failed to load audit detail:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: 'var(--rhz-bg)' }} data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <div className="flex items-center justify-between mb-10">
            <img src={logos.horizontal} alt="RHZ" className="h-8" style={logoStyle} />
            <ThemeToggle />
          </div>
          <h1 className="font-['Bruno_Ace_SC'] text-xl uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--rhz-text-primary)' }} data-testid="admin-login-title">
            Admin Panel
          </h1>
          <p className="font-['Albert_Sans'] text-sm mb-8" style={{ color: 'var(--rhz-text-secondary)' }}>
            Enter your admin key to access submissions
          </p>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              data-testid="admin-key-input"
              type="password"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              placeholder="Admin Key"
              className="rounded-sm h-12 font-['Albert_Sans']"
              style={{ backgroundColor: 'var(--rhz-input-bg)', borderColor: 'var(--rhz-border)', color: 'var(--rhz-text-primary)' }}
            />
            {loginError && (
              <p className="text-[#DC0706] text-xs font-['Albert_Sans']" data-testid="admin-login-error">{loginError}</p>
            )}
            <Button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loginLoading || !adminKey}
              className="w-full bg-[#DC0706] text-[var(--rhz-text-primary)] hover:bg-[#FE4D00] transition-colors py-5 uppercase tracking-[0.2em] font-['Bruno_Ace_SC'] text-xs rounded-sm"
            >
              {loginLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Access Dashboard"}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Audit detail view
  if (selectedAudit) {
    const audit = selectedAudit;
    const pillarScores = audit.scores?.pillar_scores || {};
    const enrichedAnswers = audit.enriched_answers || [];

    // Group answers by pillar
    const answersByPillar = {};
    enrichedAnswers.forEach((a) => {
      if (!answersByPillar[a.pillar]) answersByPillar[a.pillar] = [];
      answersByPillar[a.pillar].push(a);
    });

    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--rhz-bg)', color: 'var(--rhz-text-primary)' }} data-testid="admin-audit-detail">
        {/* Header */}
        <nav className="sticky top-0 z-50 backdrop-blur-xl" style={{ backgroundColor: 'var(--rhz-nav-bg)', borderBottom: '1px solid var(--rhz-border)' }}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <button
              data-testid="back-to-list-btn"
              onClick={() => setSelectedAudit(null)}
              className="flex items-center gap-2 text-[var(--rhz-text-secondary)] hover:text-[var(--rhz-text-primary)] transition-colors font-['Albert_Sans'] text-sm"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to Submissions
            </button>
            <div className="flex items-center gap-4">
              {audit.status === "report_ready" && (
                <button
                  data-testid="resend-webhook-btn"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/audits/${audit.audit_id}/resend-webhook`, {}, { headers: { "x-admin-key": adminKey } });
                      setTimeout(() => viewAuditDetail(audit.audit_id), 1500);
                    } catch (err) {
                      console.error("Failed to resend webhook:", err);
                    }
                  }}
                  className="text-[var(--rhz-text-secondary)] hover:text-[#DC0706] transition-colors font-['Albert_Sans'] text-xs uppercase tracking-wider"
                >
                  Resend CRM
                </button>
              )}
              <a
                data-testid="view-report-link"
                href={`/report/${audit.audit_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#DC0706] hover:text-[#FE4D00] transition-colors font-['Albert_Sans'] text-xs uppercase tracking-wider"
              >
                View Public Report
              </a>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* User info header */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-['Bruno_Ace_SC'] text-2xl uppercase tracking-[0.15em] text-[var(--rhz-text-primary)]" data-testid="detail-business-name">
                {audit.business_name}
              </h1>
              {audit.status && (
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider font-['Albert_Sans']"
                  style={{
                    backgroundColor: `${statusLabels[audit.status]?.color || "#52525B"}15`,
                    color: statusLabels[audit.status]?.color || "#52525B",
                    border: `1px solid ${statusLabels[audit.status]?.color || "#52525B"}30`
                  }}
                >
                  {statusLabels[audit.status]?.label || audit.status}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-6 text-sm text-[var(--rhz-text-secondary)] font-['Albert_Sans']">
              <span data-testid="detail-name">Name: <span className="text-[var(--rhz-text-primary)]">{audit.first_name} {audit.last_name || ""}</span></span>
              <span data-testid="detail-email">Email: <span className="text-[var(--rhz-text-primary)]">{audit.email}</span></span>
              {audit.phone && (
                <span data-testid="detail-phone">Phone: <span className="text-[var(--rhz-text-primary)]">{audit.phone}</span></span>
              )}
              <span data-testid="detail-date">Date: <span className="text-[var(--rhz-text-primary)]">{new Date(audit.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span></span>
              {audit.webhook_status && (
                <span data-testid="detail-webhook-status">
                  CRM: <span style={{ color: webhookLabels[audit.webhook_status]?.color || "#52525B" }}>
                    {webhookLabels[audit.webhook_status]?.label || audit.webhook_status}
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* Scores overview */}
          {audit.scores && (
            <div className="mb-10">
              <p className="font-['Bruno_Ace_SC'] text-xs tracking-[0.3em] uppercase text-[#DC0706] mb-4">Scorecard</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {Object.entries(pillarScores).map(([pillar, data]) => {
                  const rColor = ratingColors[data.rating] || "#DC0706";
                  return (
                    <div
                      key={pillar}
                      className="p-4 bg-[var(--rhz-surface)] border border-[var(--rhz-border)] rounded-sm"
                      data-testid={`admin-pillar-${pillar.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <p className="font-['Albert_Sans'] text-[10px] text-[var(--rhz-text-disabled)] uppercase tracking-wider mb-1 truncate">{pillar}</p>
                      <div className="flex items-baseline gap-2">
                        <span className="font-['Bruno_Ace_SC'] text-2xl" style={{ color: rColor }}>{data.score}</span>
                        <span className="text-[var(--rhz-text-disabled)] text-xs">/10</span>
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: rColor }}>{data.rating}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Answers by pillar */}
          <div className="mb-10">
            <p className="font-['Bruno_Ace_SC'] text-xs tracking-[0.3em] uppercase text-[#DC0706] mb-4">Detailed Answers</p>
            <div className="space-y-4">
              {Object.entries(answersByPillar).map(([pillar, answers]) => {
                const Icon = pillarIcons[pillar] || Target;
                return (
                  <div key={pillar} className="bg-[var(--rhz-surface)] border border-[var(--rhz-border)] rounded-sm overflow-hidden">
                    <div className="p-4 border-b border-[var(--rhz-border)] flex items-center gap-3">
                      <Icon className="h-4 w-4 text-[var(--rhz-text-secondary)]" />
                      <span className="font-['Albert_Sans'] font-bold text-sm text-[var(--rhz-text-primary)] uppercase tracking-wider">{pillar}</span>
                    </div>
                    <div className="divide-y divide-[#27272A]">
                      {answers.map((a, i) => {
                        const scoreColor = a.score >= 9 ? "#94CE77" : a.score >= 7 ? "#9CCEFF" : a.score >= 5 ? "#02808F" : a.score >= 3 ? "#FDB714" : "#DC0706";
                        return (
                          <div key={i} className="p-4 flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <p className="font-['Albert_Sans'] text-sm text-[var(--rhz-text-secondary)] mb-1">{a.question_text}</p>
                              <p className="font-['Albert_Sans'] text-sm text-[var(--rhz-text-primary)]">{a.answer_label}</p>
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <span className="font-['Bruno_Ace_SC'] text-lg" style={{ color: scoreColor }}>{a.score}</span>
                              <span className="text-[var(--rhz-text-disabled)] text-xs">/10</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Report content */}
          {audit.report && (
            <div className="mb-10">
              <p className="font-['Bruno_Ace_SC'] text-xs tracking-[0.3em] uppercase text-[#DC0706] mb-4">Generated Report</p>
              <div className="bg-[var(--rhz-surface)] border border-[var(--rhz-border)] rounded-sm p-6">
                <h3 className="font-['Albert_Sans'] font-bold text-sm text-[var(--rhz-text-primary)] uppercase tracking-wider mb-3">Executive Summary</h3>
                <p className="font-['Albert_Sans'] text-sm text-[var(--rhz-text-secondary)] leading-relaxed whitespace-pre-line mb-6">
                  {audit.report.executive_summary}
                </p>
                {audit.report.key_findings?.length > 0 && (
                  <>
                    <h3 className="font-['Albert_Sans'] font-bold text-sm text-[var(--rhz-text-primary)] uppercase tracking-wider mb-3">Key Findings</h3>
                    <div className="space-y-4 mb-6">
                      {audit.report.key_findings.map((f, i) => (
                        <div key={i} className="p-4 bg-[var(--rhz-bg)] rounded-sm border border-[#27272A]">
                          <p className="text-xs text-[#DC0706] uppercase tracking-wider font-bold mb-2">{f.pillar}</p>
                          <p className="text-sm text-[var(--rhz-text-primary)] mb-1"><span className="text-[var(--rhz-text-disabled)]">Observation:</span> {f.observation}</p>
                          <p className="text-sm text-[var(--rhz-text-secondary)] mb-1"><span className="text-[var(--rhz-text-disabled)]">Problem:</span> {f.why_problem}</p>
                          <p className="text-sm text-[#02808F]"><span className="text-[var(--rhz-text-disabled)]">Industry Norm:</span> {f.industry_norm}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {audit.report.growth_constraints?.length > 0 && (
                  <>
                    <h3 className="font-['Albert_Sans'] font-bold text-sm text-[var(--rhz-text-primary)] uppercase tracking-wider mb-3">Growth Constraints</h3>
                    <div className="space-y-2">
                      {audit.report.growth_constraints.map((c, i) => (
                        <div key={i} className="flex gap-3 p-3 bg-[var(--rhz-bg)] rounded-sm border border-[#27272A]">
                          <span className="font-['Bruno_Ace_SC'] text-sm text-[#DC0706] min-w-[1.5rem]">{c.rank || i + 1}</span>
                          <div>
                            <p className="text-sm text-[var(--rhz-text-primary)] font-bold">{c.title}</p>
                            <p className="text-sm text-[var(--rhz-text-secondary)]">{c.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main list view
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rhz-bg)', color: 'var(--rhz-text-primary)' }} data-testid="admin-dashboard">
      {/* Header */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl" style={{ backgroundColor: 'var(--rhz-nav-bg)', borderBottom: '1px solid var(--rhz-border)' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={logos.horizontal} alt="RHZ" className="h-8" style={logoStyle} />
            <span className="font-['Bruno_Ace_SC'] text-xs uppercase tracking-[0.2em]" style={{ color: 'var(--rhz-text-secondary)' }}>Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button
              data-testid="admin-logout-btn"
              onClick={() => { setAuthenticated(false); setAdminKey(""); }}
              className="flex items-center gap-2 transition-colors font-['Albert_Sans'] text-xs uppercase tracking-wider"
              style={{ color: 'var(--rhz-text-secondary)' }}
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-10">
          {[
            { label: "Total Submissions", value: stats.total, icon: Users, color: "#DC0706" },
            { label: "Reports Generated", value: stats.completed, icon: FileCheck, color: "#94CE77" },
            { label: "In Progress", value: stats.in_progress, icon: Clock, color: "#FDB714" },
          ].map((s, i) => (
            <div key={i} className="p-6 bg-[var(--rhz-surface)] border border-[var(--rhz-border)] rounded-sm" data-testid={`stat-card-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                <s.icon className="h-4 w-4" style={{ color: s.color }} />
                <span className="font-['Albert_Sans'] text-xs text-[var(--rhz-text-disabled)] uppercase tracking-wider">{s.label}</span>
              </div>
              <span className="font-['Bruno_Ace_SC'] text-3xl text-[var(--rhz-text-primary)]">{s.value}</span>
            </div>
          ))}
        </div>

        {/* Title */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-['Bruno_Ace_SC'] text-lg uppercase tracking-[0.2em] text-[var(--rhz-text-primary)]" data-testid="submissions-title">
            All Submissions
          </h2>
          <button
            data-testid="refresh-btn"
            onClick={loadAudits}
            className="text-[var(--rhz-text-secondary)] hover:text-[var(--rhz-text-primary)] transition-colors font-['Albert_Sans'] text-xs uppercase tracking-wider"
          >
            Refresh
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-20" data-testid="admin-loading">
            <Loader2 className="h-8 w-8 text-[#DC0706] animate-spin" />
          </div>
        ) : audits.length === 0 ? (
          <div className="text-center py-20" data-testid="no-submissions">
            <p className="font-['Albert_Sans'] text-[var(--rhz-text-secondary)]">No submissions yet</p>
          </div>
        ) : (
          <div className="space-y-0 sm:space-y-0" data-testid="submissions-table">
            {/* Desktop table header - hidden on mobile */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-[var(--rhz-surface)] border border-[var(--rhz-border)] rounded-t-sm text-[10px] font-['Albert_Sans'] font-bold text-[var(--rhz-text-disabled)] uppercase tracking-wider">
              <div className="col-span-3">Business</div>
              <div className="col-span-2">Contact</div>
              <div className="col-span-2">Date</div>
              <div className="col-span-1">Score</div>
              <div className="col-span-2">Rating</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1"></div>
            </div>
            {audits.map((audit, i) => {
              const statusCfg = statusLabels[audit.status] || statusLabels.created;
              const overallScore = audit.scores?.overall_score;
              const overallRating = audit.scores?.overall_rating;
              const rColor = ratingColors[overallRating] || "var(--rhz-text-disabled)";
              return (
                <div key={audit.audit_id} data-testid={`audit-row-${i}`}>
                  {/* Desktop row */}
                  <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-[var(--rhz-surface)] border-x border-b border-[var(--rhz-border)] last:rounded-b-sm hover:bg-[var(--rhz-elevated)] transition-colors items-center">
                    <div className="col-span-3">
                      <p className="font-['Albert_Sans'] text-sm text-[var(--rhz-text-primary)] font-semibold truncate">{audit.business_name}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="font-['Albert_Sans'] text-xs text-[var(--rhz-text-primary)] truncate">{audit.first_name}{audit.last_name ? ` ${audit.last_name}` : ""}</p>
                      <p className="font-['Albert_Sans'] text-xs text-[var(--rhz-text-disabled)] truncate">{audit.email}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="font-['Albert_Sans'] text-xs text-[var(--rhz-text-secondary)]">
                        {new Date(audit.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    <div className="col-span-1">
                      {overallScore != null ? (
                        <span className="font-['Bruno_Ace_SC'] text-lg" style={{ color: rColor }}>{overallScore}</span>
                      ) : <span className="text-[var(--rhz-text-disabled)] text-xs">-</span>}
                    </div>
                    <div className="col-span-2">
                      {overallRating ? (
                        <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider font-['Albert_Sans']"
                          style={{ backgroundColor: `${rColor}15`, color: rColor, border: `1px solid ${rColor}30` }}>{overallRating}</span>
                      ) : <span className="text-[var(--rhz-text-disabled)] text-xs">-</span>}
                    </div>
                    <div className="col-span-1">
                      <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider font-['Albert_Sans']"
                        style={{ backgroundColor: `${statusCfg.color}15`, color: statusCfg.color, border: `1px solid ${statusCfg.color}30` }}>{statusCfg.label}</span>
                    </div>
                    <div className="col-span-1 text-right">
                      <button data-testid={`view-detail-btn-${i}`} onClick={() => viewAuditDetail(audit.audit_id)}
                        className="text-[var(--rhz-text-secondary)] hover:text-[#DC0706] transition-colors"><Eye className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {/* Mobile card */}
                  <div className="md:hidden p-4 mb-3 bg-[var(--rhz-surface)] border border-[var(--rhz-border)] rounded-sm">
                    <div className="flex items-start justify-between mb-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-['Albert_Sans'] text-sm text-[var(--rhz-text-primary)] font-semibold truncate">{audit.business_name}</p>
                        <p className="font-['Albert_Sans'] text-xs text-[var(--rhz-text-disabled)] truncate">{audit.first_name}{audit.last_name ? ` ${audit.last_name}` : ""} &middot; {audit.email}</p>
                      </div>
                      <button data-testid={`view-detail-btn-mobile-${i}`} onClick={() => viewAuditDetail(audit.audit_id)}
                        className="ml-2 p-2 text-[var(--rhz-text-secondary)] hover:text-[#DC0706] transition-colors"><Eye className="h-4 w-4" /></button>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {overallScore != null && (
                        <span className="font-['Bruno_Ace_SC'] text-lg" style={{ color: rColor }}>{overallScore}/10</span>
                      )}
                      {overallRating && (
                        <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider font-['Albert_Sans']"
                          style={{ backgroundColor: `${rColor}15`, color: rColor, border: `1px solid ${rColor}30` }}>{overallRating}</span>
                      )}
                      <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider font-['Albert_Sans']"
                        style={{ backgroundColor: `${statusCfg.color}15`, color: statusCfg.color, border: `1px solid ${statusCfg.color}30` }}>{statusCfg.label}</span>
                      <span className="font-['Albert_Sans'] text-[10px] text-[var(--rhz-text-disabled)] ml-auto">
                        {new Date(audit.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
